import React, { useMemo } from 'react';

import { Alert, Box, Link, SpaceBetween, StatusIndicator, TextContent } from '@amzn/awsui-components-react-v3';

import { Serial } from '../../store/types';
import CopyText from '../CopyText';
import { WithExceptions } from '../ExceptionsModal/WithExceptions';
import ScanInput from '../ScanInput';
import { GetExeceptionURL } from '../Utils';
import { getUnifiedReceiveUrl } from '../Utils/Utils';
import { MOBILITY_TICKET_URL, SERIAL_ERROR_CODES, SERIAL_ERROR_CODES_MAPPING, WARNING_ERROR_CODES } from './constants';

interface DuplicateSerialIDAlertProps {
  serialNumber: string;
  onCancel: () => void;
  onRescan: (val: string) => void;
  code?: string;
}

interface SerialErrorProps {
  code: string;
  serial: Serial;
  onCancel?: any;
  onRescan: any;
}

export const SerialExists = () => {
  return (
    <Box>
      <StatusIndicator type="error">Serial ID already exists</StatusIndicator>
    </Box>
  );
};

export const SerialCantReceive = ({ serial }: { serial: Serial }) => {
  return (
    <Box>
      <SpaceBetween size="s">
        <TextContent>
          <p>Composite duplicate serial encountered</p>
        </TextContent>
      </SpaceBetween>
    </Box>
  );
};

export const SerialNotExpectedAsn = () => {
  return (
    <TextContent>
      <p>
        A part with this serial number is not expected in the shipment. Visually verify the barcode scanned on the
        physical component, is the correct serial barcode.
      </p>
    </TextContent>
  );
};

export const SerialIDExpectedInShipment = ({ serial }: { serial: Serial }) => {
  const externalRef: string = serial?.externalReferenceValue || '';
  const serialNumber: string = serial?.serialNumber;
  return (
    <Box>
      <p>
        This serial {serialNumber} belongs to Shipment ID{' '}
        <CopyText label="Shipment ID" value={externalRef}>
          {externalRef}
        </CopyText>
        . Use <Link href={getUnifiedReceiveUrl()}>Unified Receive </Link> to ingest this part into inventory.
      </p>
    </Box>
  );
};

export const DuplicateSerialID = ({ serial }: { serial: Serial }) => {
  return (
    <Box>
      Duplicate serial number {serial.siteSpecificSerialId || serial.serialNumber} scanned, serial already scanned
      inside container {serial.containerId}
    </Box>
  );
};

export const MobilityUnavailable = () => {
  return (
    <Box>
      Mobility Service is currently experiencing a temporary issue. Please try again in a few minutes. If the issue
      persists, please cut a ticket <a href={MOBILITY_TICKET_URL}>here</a>.
    </Box>
  );
};

export const SerialAlreadyReceivedForShipment = ({ serial }: { serial: Serial }) => {
  const serialNumber: string = serial.siteSpecificSerialId || serial.serialNumber;
  return <Box>The serial number {serialNumber} has already been received for this shipment.</Box>;
};

export const CompositeDuplicateBadStateSerialID = ({ serial }: { serial: Serial }) => {
  return (
    <Box>
      Duplicate serial number {serial.siteSpecificSerialId || serial.serialNumber} scanned, serial state is invalid in
      mobility to recieve.
    </Box>
  );
};

export const TownsendIdentifierCombinationInvalid = () => {
  return <Box>Townsend catalog identifier(s) (such as MPN, IPN, and manufacturer) are incorrect.</Box>;
};

export const CompositeDuplicateSerialID = ({ serial }: { serial: Serial }) => {
  const referenceId: string = serial.request?.referenceId || '';

  return (
    <Alert statusIconAriaLabel="Warning" type="error">
      <SpaceBetween size="s">
        <Box variant="span">{`${serial.serialNumber} already exists in our records and cannot be received`}</Box>
        <GetExeceptionURL referenceId={referenceId} path="create" title="Create exception" />
      </SpaceBetween>
    </Alert>
  );
};

export const DuplicateSerialIDAlert = ({ serialNumber, onCancel, onRescan, code }: DuplicateSerialIDAlertProps) => {
  const [checked] = React.useState(true);

  const title = useMemo(() => {
    if (code === SERIAL_ERROR_CODES.SERIAL_SPECIAL_CHARACTERS) {
      return <h5>Spaces are not allowed</h5>;
    }

    return <h5>You entered a duplicate serial {serialNumber}</h5>;
  }, [serialNumber, code]);

  return (
    <div>
      <TextContent>{title}</TextContent>
      <Box>
        <Box> Enter site-specific label to continue receive this part</Box>
        {checked && (
          <ScanInput
            placeholder="Enter site-specific label"
            ariaLabel="site-specific-input"
            controlId="scan-site-specific-input"
            validateRegex="^[a-zA-Z0-9_.,-]*$"
            validationErrorText={'Site specific label should not contain spaces or special characters'}
            onScan={onRescan}
            clearOnSubmit
            autoFocus={false}
          />
        )}
      </Box>
    </div>
  );
};

export const ErrorMessage = ({ code, serial, onRescan, onCancel }: SerialErrorProps) => {
  if (
    code === SERIAL_ERROR_CODES.serial_id_composite_duplicate ||
    code === SERIAL_ERROR_CODES.COMPOSITE_DUPLICATE_SERIAL
  ) {
    return (
      <div className="serial-error-container">
        <SerialCantReceive serial={serial} />
      </div>
    );
  }

  if (code === SERIAL_ERROR_CODES.TOWNSEND_IDENTIFIER_COMBINATION_INVALID) {
    return (
      <div className="serial-error-container">
        <TownsendIdentifierCombinationInvalid />
      </div>
    );
  }

  if (code === SERIAL_ERROR_CODES.SERIAL_ALREADY_RECEIVED_FOR_SHIPMENT) {
    return <SerialAlreadyReceivedForShipment serial={serial} />;
  }

  if (code === SERIAL_ERROR_CODES.MOBILITY_SERVICE_NOT_AVAILABLE) {
    return (
      <div className="serial-error-container">
        <MobilityUnavailable />
      </div>
    );
  }

  if (
    code === SERIAL_ERROR_CODES.SERIAL_ALREADY_EXISTS ||
    code === SERIAL_ERROR_CODES.serial_id_cannot_receive ||
    code === SERIAL_ERROR_CODES.SERIAL_SPECIAL_CHARACTERS
  ) {
    return (
      <div className="serial-error-container">
        <DuplicateSerialIDAlert
          serialNumber={serial?.serialNumber}
          onCancel={onCancel}
          onRescan={(val) => onRescan(serial, val)}
          code={code}
        />
      </div>
    );
  }

  if (code === SERIAL_ERROR_CODES.COMPOSITE_DUPLICATE_SERIAL_INVALID_STATE) {
    return (
      <div className="serial-error-container">
        <CompositeDuplicateBadStateSerialID serial={serial} />
      </div>
    );
  }

  if (code === SERIAL_ERROR_CODES.SERIAL_EXPECTED_IN_SHIPMENT) {
    return (
      <div className="serial-error-container">
        <SerialIDExpectedInShipment serial={serial} />
      </div>
    );
  }

  if (WARNING_ERROR_CODES.includes(code)) {
    return <SerialNotExpectedAsn />;
  }

  if (code === SERIAL_ERROR_CODES.serial_id_duplicate) {
    return <DuplicateSerialID serial={serial} />;
  }

  return <>{SERIAL_ERROR_CODES_MAPPING[code] || SERIAL_ERROR_CODES_MAPPING.network_error} </>;
};

export const ErrorMessages = WithExceptions(ErrorMessage);
